import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/shared/state/authstore'
import {
  clockInToOffice,
  clockOutOfOffice,
  createOfficeTime,
  deleteOfficeTime,
  fetchOfficeClockStatus,
  fetchOfficeTime,
  updateOfficeTime,
} from '../api/officeTimeApi'
import type {
  CreateOfficeTimeRequest,
  ListOfficeTimeParams,
  OfficeClockRequest,
  UpdateOfficeTimeRequest,
} from './types'

export const officeTimeKeys = {
  all: ['office-time'] as const,
  status: (accountId: number | null) => [...officeTimeKeys.all, 'status', accountId] as const,
  lists: () => [...officeTimeKeys.all, 'list'] as const,
  list: (params: ListOfficeTimeParams) => [...officeTimeKeys.lists(), params] as const,
}

const CLOCK_STATUS_STALE_MS = 15_000

export function useOfficeClockStatus() {
  const accountId = useAuthStore((s) => s.accountId)
  const accessToken = useAuthStore((s) => s.accessToken)
  return useQuery({
    queryKey: officeTimeKeys.status(accountId),
    queryFn: fetchOfficeClockStatus,
    enabled: Boolean(accessToken) && accountId !== null,
    staleTime: CLOCK_STATUS_STALE_MS,
    refetchOnWindowFocus: true,
    retry: false,
  })
}

export function useClockIn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OfficeClockRequest) => clockInToOffice(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: officeTimeKeys.all }),
  })
}

export function useClockOut() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OfficeClockRequest) => clockOutOfOffice(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: officeTimeKeys.all }),
  })
}

export function useOfficeTime(params: ListOfficeTimeParams | null) {
  return useQuery({
    queryKey: officeTimeKeys.list(params ?? { from: '', to: '' }),
    queryFn: () => fetchOfficeTime(params!),
    enabled: params !== null,
    staleTime: 0,
    retry: false,
  })
}

export function useCreateOfficeTime() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateOfficeTimeRequest) => createOfficeTime(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: officeTimeKeys.all }),
  })
}

export function useUpdateOfficeTime() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateOfficeTimeRequest }) => updateOfficeTime(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: officeTimeKeys.all }),
  })
}

export function useDeleteOfficeTime() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteOfficeTime(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: officeTimeKeys.all }),
  })
}
